/**
 * PPP (Purchasing Power Parity) Pricing Utilities
 * Shared between app.js and tests
 */

import { settings } from "../webapp/js/settings.js";

// Valid ISO 3166-1 alpha-3 country codes
export const VALID_COUNTRY_CODES = new Set([
  "AFG",
  "ALB",
  "DZA",
  "ASM",
  "AND",
  "AGO",
  "AIA",
  "ATG",
  "ARG",
  "ARM",
  "ABW",
  "AUS",
  "AUT",
  "AZE",
  "BHS",
  "BHR",
  "BGD",
  "BRB",
  "BLR",
  "BEL",
  "BLZ",
  "BEN",
  "BMU",
  "BTN",
  "BOL",
  "BIH",
  "BWA",
  "BRA",
  "BRN",
  "BGR",
  "BFA",
  "BDI",
  "KHM",
  "CMR",
  "CAN",
  "CPV",
  "CYM",
  "CAF",
  "TCD",
  "CHL",
  "CHN",
  "COL",
  "COM",
  "COG",
  "COD",
  "CRI",
  "CIV",
  "HRV",
  "CUB",
  "CUW",
  "CYP",
  "CZE",
  "DNK",
  "DJI",
  "DMA",
  "DOM",
  "ECU",
  "EGY",
  "SLV",
  "GNQ",
  "ERI",
  "EST",
  "SWZ",
  "ETH",
  "FLK",
  "FRO",
  "FJI",
  "FIN",
  "FRA",
  "GUF",
  "PYF",
  "GAB",
  "GMB",
  "GEO",
  "DEU",
  "GHA",
  "GIB",
  "GRC",
  "GRL",
  "GRD",
  "GLP",
  "GUM",
  "GTM",
  "GGY",
  "GIN",
  "GNB",
  "GUY",
  "HTI",
  "HND",
  "HKG",
  "HUN",
  "ISL",
  "IND",
  "IDN",
  "IRN",
  "IRQ",
  "IRL",
  "IMN",
  "ISR",
  "ITA",
  "JAM",
  "JPN",
  "JEY",
  "JOR",
  "KAZ",
  "KEN",
  "KIR",
  "PRK",
  "KOR",
  "KWT",
  "KGZ",
  "LAO",
  "LVA",
  "LBN",
  "LSO",
  "LBR",
  "LBY",
  "LIE",
  "LTU",
  "LUX",
  "MAC",
  "MDG",
  "MWI",
  "MYS",
  "MDV",
  "MLI",
  "MLT",
  "MHL",
  "MTQ",
  "MRT",
  "MUS",
  "MYT",
  "MEX",
  "FSM",
  "MDA",
  "MCO",
  "MNG",
  "MNE",
  "MSR",
  "MAR",
  "MOZ",
  "MMR",
  "NAM",
  "NRU",
  "NPL",
  "NLD",
  "NCL",
  "NZL",
  "NIC",
  "NER",
  "NGA",
  "NIU",
  "NFK",
  "MKD",
  "MNP",
  "NOR",
  "OMN",
  "PAK",
  "PLW",
  "PSE",
  "PAN",
  "PNG",
  "PRY",
  "PER",
  "PHL",
  "PCN",
  "POL",
  "PRT",
  "PRI",
  "QAT",
  "REU",
  "ROU",
  "RUS",
  "RWA",
  "BLM",
  "SHN",
  "KNA",
  "LCA",
  "MAF",
  "SPM",
  "VCT",
  "WSM",
  "SMR",
  "STP",
  "SAU",
  "SEN",
  "SRB",
  "SYC",
  "SLE",
  "SGP",
  "SXM",
  "SVK",
  "SVN",
  "SLB",
  "SOM",
  "ZAF",
  "SGS",
  "SSD",
  "ESP",
  "LKA",
  "SDN",
  "SUR",
  "SJM",
  "SWE",
  "CHE",
  "SYR",
  "TWN",
  "TJK",
  "TZA",
  "THA",
  "TLS",
  "TGO",
  "TKL",
  "TON",
  "TTO",
  "TUN",
  "TUR",
  "TKM",
  "TCA",
  "TUV",
  "UGA",
  "UKR",
  "ARE",
  "GBR",
  "USA",
  "UMI",
  "URY",
  "UZB",
  "VUT",
  "VEN",
  "VNM",
  "VGB",
  "VIR",
  "WLF",
  "ESH",
  "YEM",
  "ZMB",
  "ZWE",
]);

// Country name overrides
export const COUNTRY_NAME_OVERRIDES = {
  "West Bank and Gaza": "Palestine",
};

/**
 * Parse a CSV line properly handling quoted fields
 */
export function parseCSVLine(line) {
  const result = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      result.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current);
  return result;
}

/**
 * Find latest available GDP value from CSV parts
 */
export function getLatestGdp(parts) {
  // Start from the end (most recent year) and work backwards
  for (let i = parts.length - 1; i >= 4; i--) {
    const value = parseFloat(parts[i]);
    if (value && !isNaN(value) && value > 0) {
      return value;
    }
  }
  return null;
}

/**
 * Load PPP data from CSV file
 * @param {string} csvContent - The CSV file content
 * @returns {Array<{name: string, code: string, gdp: number, multiplier: number}>}
 */
export function loadPPPData(csvContent) {
  const lines = csvContent.split("\n");

  // Find US GDP per capita
  const usLine = lines.find((line) => line.includes('"United States"'));
  if (!usLine) throw new Error("US data not found");

  const usParts = parseCSVLine(usLine);
  const usGdp = getLatestGdp(usParts);

  if (!usGdp || isNaN(usGdp)) {
    throw new Error("Invalid US GDP data");
  }

  // Parse all countries
  const countries = [];
  for (const line of lines) {
    if (
      !line.includes("GDP per capita, PPP") ||
      line.startsWith('"Data Source')
    )
      continue;

    const parts = parseCSVLine(line);
    if (parts.length < 5) continue;

    let countryName = parts[0];
    const countryCode = parts[1];

    // Apply country name override if exists
    if (COUNTRY_NAME_OVERRIDES[countryName]) {
      countryName = COUNTRY_NAME_OVERRIDES[countryName];
    }

    // Skip if not a valid country code
    if (!VALID_COUNTRY_CODES.has(countryCode)) {
      continue;
    }

    const gdp = getLatestGdp(parts);

    // Skip if no valid GDP data
    if (!gdp) {
      continue;
    }

    // Calculate PPP multiplier (ratio of country GDP to US GDP)
    const multiplier = gdp / usGdp;

    countries.push({
      name: countryName,
      code: countryCode,
      gdp: gdp,
      multiplier: multiplier,
    });
  }

  return countries;
}

/**
 * Fill economies that the World Bank file leaves blank.
 * A positive GDP already loaded from the file wins. `gdp` must be current
 * international dollars on that file's US scale.
 * @param {Array<{code: string, name: string, gdp: number, multiplier: number}>} countries
 * @param {Array<{code: string, name?: string, gdp: number}>} overrides
 * @param {number} usGdp
 */
export function applyPppOverrides(countries, overrides, usGdp) {
  const present = new Set(countries.map((country) => country.code));
  for (const row of overrides || []) {
    if (!row || present.has(row.code) || !VALID_COUNTRY_CODES.has(row.code)) {
      continue;
    }
    const gdp = Number(row.gdp);
    if (!(gdp > 0) || !(usGdp > 0)) continue;
    countries.push({
      name: row.name || row.code,
      code: row.code,
      gdp,
      multiplier: gdp / usGdp,
    });
    present.add(row.code);
  }
  return countries;
}

/**
 * Calculate adjusted price with PPP interpolation
 * @param {number} basePrice - Base price in USD
 * @param {number} multiplier - PPP multiplier for the country
 * @returns {number}
 */
/** Interpolated PPP multiplier, never below the US-price floor. */
export function pppMultiplier(rawMultiplier) {
  const interpolated =
    1.0 + (Number(rawMultiplier) - 1.0) * settings.pppInterpolation;
  return Math.max(settings.pppMultiplierFloor, interpolated);
}

export function calculatePrice(basePrice, multiplier) {
  return basePrice * pppMultiplier(multiplier);
}

/**
 * Stripe country code mapping (ISO 3166-1 alpha-2 to alpha-3)
 * Stripe uses 2-letter codes, we use 3-letter codes for PPP
 */
export const STRIPE_TO_PPP_COUNTRY = {
  AF: "AFG",
  AL: "ALB",
  DZ: "DZA",
  AS: "ASM",
  AD: "AND",
  AO: "AGO",
  AI: "AIA",
  AG: "ATG",
  AR: "ARG",
  AM: "ARM",
  AW: "ABW",
  AU: "AUS",
  AT: "AUT",
  AZ: "AZE",
  BS: "BHS",
  BH: "BHR",
  BD: "BGD",
  BB: "BRB",
  BY: "BLR",
  BE: "BEL",
  BZ: "BLZ",
  BJ: "BEN",
  BM: "BMU",
  BT: "BTN",
  BO: "BOL",
  BA: "BIH",
  BW: "BWA",
  BR: "BRA",
  BN: "BRN",
  BG: "BGR",
  BF: "BFA",
  BI: "BDI",
  KH: "KHM",
  CM: "CMR",
  CA: "CAN",
  CV: "CPV",
  KY: "CYM",
  CF: "CAF",
  TD: "TCD",
  CL: "CHL",
  CN: "CHN",
  CO: "COL",
  KM: "COM",
  CG: "COG",
  CD: "COD",
  CR: "CRI",
  CI: "CIV",
  HR: "HRV",
  CU: "CUB",
  CW: "CUW",
  CY: "CYP",
  CZ: "CZE",
  DK: "DNK",
  DJ: "DJI",
  DM: "DMA",
  DO: "DOM",
  EC: "ECU",
  EG: "EGY",
  SV: "SLV",
  GQ: "GNQ",
  ER: "ERI",
  EE: "EST",
  SZ: "SWZ",
  ET: "ETH",
  FK: "FLK",
  FO: "FRO",
  FJ: "FJI",
  FI: "FIN",
  FR: "FRA",
  GF: "GUF",
  PF: "PYF",
  GA: "GAB",
  GM: "GMB",
  GE: "GEO",
  DE: "DEU",
  GH: "GHA",
  GI: "GIB",
  GR: "GRC",
  GL: "GRL",
  GD: "GRD",
  GP: "GLP",
  GU: "GUM",
  GT: "GTM",
  GG: "GGY",
  GN: "GIN",
  GW: "GNB",
  GY: "GUY",
  HT: "HTI",
  HN: "HND",
  HK: "HKG",
  HU: "HUN",
  IS: "ISL",
  IN: "IND",
  ID: "IDN",
  IR: "IRN",
  IQ: "IRQ",
  IE: "IRL",
  IM: "IMN",
  IL: "ISR",
  IT: "ITA",
  JM: "JAM",
  JP: "JPN",
  JE: "JEY",
  JO: "JOR",
  KZ: "KAZ",
  KE: "KEN",
  KI: "KIR",
  KP: "PRK",
  KR: "KOR",
  KW: "KWT",
  KG: "KGZ",
  LA: "LAO",
  LV: "LVA",
  LB: "LBN",
  LS: "LSO",
  LR: "LBR",
  LY: "LBY",
  LI: "LIE",
  LT: "LTU",
  LU: "LUX",
  MO: "MAC",
  MG: "MDG",
  MW: "MWI",
  MY: "MYS",
  MV: "MDV",
  ML: "MLI",
  MT: "MLT",
  MH: "MHL",
  MQ: "MTQ",
  MR: "MRT",
  MU: "MUS",
  YT: "MYT",
  MX: "MEX",
  FM: "FSM",
  MD: "MDA",
  MC: "MCO",
  MN: "MNG",
  ME: "MNE",
  MS: "MSR",
  MA: "MAR",
  MZ: "MOZ",
  MM: "MMR",
  NA: "NAM",
  NR: "NRU",
  NP: "NPL",
  NL: "NLD",
  NC: "NCL",
  NZ: "NZL",
  NI: "NIC",
  NE: "NER",
  NG: "NGA",
  NU: "NIU",
  NF: "NFK",
  MK: "MKD",
  MP: "MNP",
  NO: "NOR",
  OM: "OMN",
  PK: "PAK",
  PW: "PLW",
  PS: "PSE",
  PA: "PAN",
  PG: "PNG",
  PY: "PRY",
  PE: "PER",
  PH: "PHL",
  PN: "PCN",
  PL: "POL",
  PT: "PRT",
  PR: "PRI",
  QA: "QAT",
  RE: "REU",
  RO: "ROU",
  RU: "RUS",
  RW: "RWA",
  BL: "BLM",
  SH: "SHN",
  KN: "KNA",
  LC: "LCA",
  MF: "MAF",
  PM: "SPM",
  VC: "VCT",
  WS: "WSM",
  SM: "SMR",
  ST: "STP",
  SA: "SAU",
  SN: "SEN",
  RS: "SRB",
  SC: "SYC",
  SL: "SLE",
  SG: "SGP",
  SX: "SXM",
  SK: "SVK",
  SI: "SVN",
  SB: "SLB",
  SO: "SOM",
  ZA: "ZAF",
  GS: "SGS",
  SS: "SSD",
  ES: "ESP",
  LK: "LKA",
  SD: "SDN",
  SR: "SUR",
  SJ: "SJM",
  SE: "SWE",
  CH: "CHE",
  SY: "SYR",
  TW: "TWN",
  TJ: "TJK",
  TZ: "TZA",
  TH: "THA",
  TL: "TLS",
  TG: "TGO",
  TK: "TKL",
  TO: "TON",
  TT: "TTO",
  TN: "TUN",
  TR: "TUR",
  TM: "TKM",
  TC: "TCA",
  TV: "TUV",
  UG: "UGA",
  UA: "UKR",
  AE: "ARE",
  GB: "GBR",
  US: "USA",
  UM: "UMI",
  UY: "URY",
  UZ: "UZB",
  VU: "VUT",
  VE: "VEN",
  VN: "VNM",
  VG: "VGB",
  VI: "VIR",
  WF: "WLF",
  EH: "ESH",
  YE: "YEM",
  ZM: "ZMB",
  ZW: "ZWE",
};

/**
 * Convert Stripe country code (alpha-2) to PPP country code (alpha-3)
 * @param {string} stripeCountryCode - 2-letter ISO code from Stripe
 * @returns {string|null} - 3-letter ISO code or null if not found
 */
export function stripeCountryToPPP(stripeCountryCode) {
  return STRIPE_TO_PPP_COUNTRY[stripeCountryCode] || null;
}

/**
 * Reverse mapping: PPP country code (alpha-3) to Stripe (alpha-2)
 * Built lazily on first call
 */
let PPP_TO_STRIPE_COUNTRY = null;

/**
 * Convert PPP country code (alpha-3) to Stripe country code (alpha-2)
 * @param {string} pppCountryCode - 3-letter ISO code
 * @returns {string|null} - 2-letter ISO code or null if not found
 */
export function pppCountryToStripe(pppCountryCode) {
  if (!PPP_TO_STRIPE_COUNTRY) {
    PPP_TO_STRIPE_COUNTRY = Object.fromEntries(
      Object.entries(STRIPE_TO_PPP_COUNTRY).map(([k, v]) => [v, k])
    );
  }
  return PPP_TO_STRIPE_COUNTRY[pppCountryCode] || null;
}
